import React, { useState } from "react";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { loadModule } from "../store/features/moduleSlice";
import { saveModuleDesign, loadModuleDesign } from "../utils/moduleSaveLoad";

interface ModuleSaveLoadButtonsProps {
  className?: string;
  variant?: "compact" | "full";
}

export const ModuleSaveLoadButtons: React.FC<ModuleSaveLoadButtonsProps> = ({ 
  className = "", 
  variant = "full" 
}) => {
  const dispatch = useAppDispatch();
  const moduleState = useAppSelector((state) => state.module);
  const [isLoading, setIsLoading] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [customFilename, setCustomFilename] = useState("");

  // Handle escape key to close dialog
  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && showSaveDialog) {
        setShowSaveDialog(false);
        setCustomFilename("");
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [showSaveDialog]);

  const handleSave = () => {
    if (moduleState.moduleConfig) {
      // Convert ModuleConfiguration to ModuleItem format for saving
      const moduleItem = {
        name: moduleState.moduleConfig.name,
        tonnage: moduleState.moduleConfig.size,
        techLevel: moduleState.moduleConfig.techLevel,
        description: moduleState.moduleConfig.description || "",
        fitoutCost: moduleState.moduleConfig.calculations.totalCost,
        fitoutWeight: moduleState.moduleConfig.calculations.totalWeight,
        fitoutPower: moduleState.moduleConfig.calculations.totalPower,
        moduleConfig: {
          optionalSystems: moduleState.moduleConfig.systems.optionalSystems,
          powerPlant: moduleState.moduleConfig.systems.powerPlant,
          computer: moduleState.moduleConfig.systems.computer,
          computerOptions: moduleState.moduleConfig.systems.computerOptions,
          software: moduleState.moduleConfig.systems.software,
          sensors: moduleState.moduleConfig.systems.sensors,
          weapons: moduleState.moduleConfig.systems.weapons,
          weaponMounts: moduleState.moduleConfig.systems.weaponMounts,
          defensiveSystems: moduleState.moduleConfig.systems.defensiveSystems,
          ammunitionStorage: moduleState.moduleConfig.systems.ammunitionStorage,
          drives: moduleState.moduleConfig.systems.drives,
          crew: moduleState.moduleConfig.systems.crew,
          staterooms: moduleState.moduleConfig.systems.staterooms,
          stateroomConfig: moduleState.moduleConfig.systems.stateroomConfig,
          cargo: moduleState.moduleConfig.systems.cargo,
          fuel: moduleState.moduleConfig.systems.fuel,
          supplyUnits: moduleState.moduleConfig.supplyUnits,
          supplyTons: moduleState.moduleConfig.supplyTons,
        },
        installed: false,
        exportedAt: new Date().toISOString(),
        version: "1.0.0",
        exportFormat: "tm2",
      };

      saveModuleDesign(moduleItem, customFilename || undefined);
      setShowSaveDialog(false);
      setCustomFilename("");
    } else {
      alert("No module data to save. Please configure a module first.");
    }
  };

  const handleLoad = async () => {
    setIsLoading(true);
    try {
      const loadedModule = await loadModuleDesign();
      if (loadedModule) {
        // Convert ModuleItem to ModuleConfiguration format
        const moduleConfiguration = {
          name: loadedModule.name,
          size: loadedModule.tonnage,
          techLevel: loadedModule.techLevel || 12,
          description: loadedModule.description || "",
          systems: {
            optionalSystems: loadedModule.moduleConfig?.optionalSystems || [],
            powerPlant: loadedModule.moduleConfig?.powerPlant ? [loadedModule.moduleConfig.powerPlant] : [],
            computer: loadedModule.moduleConfig?.computer || [],
            computerOptions: loadedModule.moduleConfig?.computerOptions || {},
            software: loadedModule.moduleConfig?.software || {
              installed: [
                { packageId: "manoeuvre", level: 0, installed: true },
                { packageId: "intellect", level: 0, installed: true },
                { packageId: "library", level: 0, installed: true },
              ],
              running: [
                { packageId: "manoeuvre", level: 0, running: true },
                { packageId: "intellect", level: 0, running: true },
                { packageId: "library", level: 0, running: true },
              ],
              expertSkills: {},
              translatorLanguages: {},
              databaseTopics: {},
              virtualCrewRoles: {},
              virtualGunnerRoles: {},
            },
            sensors: loadedModule.moduleConfig?.sensors,
            weapons: loadedModule.moduleConfig?.weapons || [],
            weaponMounts: loadedModule.moduleConfig?.weaponMounts || [],
            defensiveSystems: loadedModule.moduleConfig?.defensiveSystems || [],
            ammunitionStorage: loadedModule.moduleConfig?.ammunitionStorage || [],
            drives: loadedModule.moduleConfig?.drives || [],
            crew: loadedModule.moduleConfig?.crew,
            staterooms: loadedModule.moduleConfig?.staterooms || [],
            stateroomConfig: loadedModule.moduleConfig?.stateroomConfig,
            cargo: loadedModule.moduleConfig?.cargo,
            fuel: loadedModule.moduleConfig?.fuel,
          },
          calculations: {
            totalCost: loadedModule.fitoutCost || 0,
            totalWeight: loadedModule.fitoutWeight || 0,
            totalPower: loadedModule.fitoutPower || 0,
            remainingTons: loadedModule.tonnage - (loadedModule.fitoutWeight || 0),
            crewRequirements: 0,
            totalCrew: 0,
            totalPassengers: 0,
            monthlyCost: 0,
          },
          supplyUnits: loadedModule.moduleConfig?.supplyUnits,
          supplyTons: loadedModule.moduleConfig?.supplyTons,
        };

        dispatch(loadModule({
          currentStep: 0,
          moduleData: loadedModule,
          moduleConfig: moduleConfiguration,
          validation: {
            isValid: true,
            errors: [],
            warnings: [],
          },
          isDirty: false,
          lastSaved: null,
          version: "1.0.0",
        }));

        console.log("Loaded module:", loadedModule);
      }
    } catch (error) {
      console.error("Error loading module design:", error);
      alert("Error loading module design. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (variant === "compact") {
    return (
      <div className={`flex gap-2 ${className}`}>
        <button
          onClick={handleSave}
          className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors flex items-center gap-1"
          title="Save module design as .TM2 file"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
          </svg>
          Save
        </button>
        <button
          onClick={handleLoad}
          disabled={isLoading}
          className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
          title="Load module design from .TM2 file"
        >
          {isLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
          )}
          Load
        </button>
      </div>
    );
  }

  return (
    <>
      <div className={`flex flex-col sm:flex-row gap-3 ${className}`}>
        <div className="flex-1">
          <button
            onClick={() => setShowSaveDialog(true)}
            className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
            </svg>
            Save Module Design (.TM2)
          </button>
          <p className="text-xs text-gray-600 mt-1 text-center">
            Save your current module design to a file
          </p>
        </div>
        
        <div className="flex-1">
          <button
            onClick={handleLoad}
            disabled={isLoading}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
              </svg>
            )}
            Load Module Design (.TM2)
          </button>
          <p className="text-xs text-gray-600 mt-1 text-center">
            Load a previously saved module design
          </p>
        </div>
      </div>

      {/* Save Dialog */}
      {showSaveDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Save Module Design</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filename (optional)
              </label>
              <input
                type="text"
                value={customFilename}
                onChange={(e) => setCustomFilename(e.target.value)}
                placeholder="Enter custom filename"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave blank to use default: {moduleState.moduleConfig?.name || "module"}_{new Date().toISOString().split('T')[0]}.TM2
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowSaveDialog(false);
                  setCustomFilename("");
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
